/**
 * Cliente HTTP del player.
 * Maneja Bearer token, errores, parseo JSON.
 */

import { loadAuthToken } from './storage';

const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3010').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public override readonly message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  authenticated?: boolean;
  signal?: AbortSignal;
  /** Override del Bearer token (usado en pairing antes de tener token guardado) */
  bearer?: string;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  if (opts.authenticated !== false) {
    const token = opts.bearer ?? loadAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/api${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    // No mandamos cookies del navegador
    credentials: 'omit',
  });

  let parsed: unknown = null;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    parsed = await res.json().catch(() => null);
  } else if (res.status !== 204) {
    parsed = await res.text().catch(() => null);
  }

  if (!res.ok) {
    const body = parsed as { message?: string | string[] } | null;
    const message = Array.isArray(body?.message)
      ? body!.message.join('; ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

// ============================================================
// Endpoints específicos del player
// ============================================================

export interface PairInitResponse {
  pairingCode: string;
  expiresAt: string;
}

export interface PairStatusResponse {
  paired: boolean;
  authToken?: string;
  screenId?: string;
}

export interface PlayerConfig {
  contentVersion: string;
  screenId: string;
  locationId: string;
  organizationId: string;
  active: ActiveContent | null;
  alert: AlertOverlay | null;
}

export type ActiveContent =
  | {
      type: 'PLAYLIST';
      source: 'TAKEOVER' | 'CAMPAIGN' | 'RECURRING' | 'FALLBACK';
      sourceId: string;
      playlist: Playlist;
    }
  | {
      type: 'HDMI_INPUT';
      source: 'TAKEOVER';
      sourceId: string;
      hdmiInput: 'HDMI1' | 'HDMI2' | 'HDMI3';
    };

export interface Playlist {
  id: string;
  name: string;
  defaultDurationSeconds: number;
  items: PlaylistItem[];
}

export interface PlaylistItem {
  id: string;
  mediaAssetId: string;
  order: number;
  durationSeconds: number;
  transitionEffect: 'NONE' | 'FADE' | 'SLIDE_LEFT' | 'SLIDE_RIGHT' | 'ZOOM';
  mediaAsset: {
    id: string;
    type: 'IMAGE' | 'VIDEO';
    mimeType: string;
    cdnUrl: string;
    width: number | null;
    height: number | null;
    durationSeconds: number | null;
  };
}

export interface AlertOverlay {
  id: string;
  text: string;
  icon: string;
  startsAt: string;
  expiresAt: string | null;
}

export interface PlaybackEntry {
  mediaAssetId: string | null;
  playlistId: string | null;
  playedAt: string;
  durationPlayedSeconds: number;
  completed: boolean;
}

export const api = {
  // ---- pairing (sin auth) ----
  pairInit: (deviceId: string, tizenVersion?: string) =>
    request<PairInitResponse>('/screens/pair-init', {
      method: 'POST',
      authenticated: false,
      body: { deviceId, tizenVersion },
    }),

  pairStatus: (deviceId: string) =>
    request<PairStatusResponse>(`/screens/pair-status/${encodeURIComponent(deviceId)}`, {
      authenticated: false,
    }),

  // ---- post-pairing (con Bearer token) ----
  getConfig: (signal?: AbortSignal) =>
    request<PlayerConfig>('/player/config', { signal }),

  ingestPlayback: (entries: PlaybackEntry[]) =>
    request<{ count: number }>('/playback-logs/ingest', {
      method: 'POST',
      body: { entries },
    }),
};
